import type { NostrEvent } from '@nostrify/nostrify';

/** NIP-84 highlight (regular). Content is the exact highlighted text. */
export const HIGHLIGHT_KIND = 9802;

/** The minimal identity of an event needed to build a highlight or query highlights against it. */
export interface HighlightTarget {
  id: string;
  pubkey: string;
  kind: number;
  /** `kind:pubkey:d` coordinate, present when `kind` is addressable (30000-39999). */
  addressPointer?: string;
}

/**
 * Builds the tags for a NIP-84 highlight of `target`. Includes both an
 * `a` tag (when addressable — ties the highlight to the *specific
 * author's version* being read, matching how stars/zaps/comments treat
 * every version independently) and an `e` tag with the exact event id,
 * per NIP-84: "Events SHOULD tag the source of the highlight... `a` or
 * `e` tags should be used for nostr events." A `p` tag attributes the
 * highlighted material to its author (role `"author"`, per spec).
 *
 * `context` is optional surrounding text (e.g. the containing
 * paragraph) — NIP-84: "Clients MAY include a `context` tag, useful
 * when the highlight is a subset of a paragraph."
 */
export function buildHighlightTags(target: HighlightTarget, context?: string): string[][] {
  const tags: string[][] = [];
  if (target.addressPointer) tags.push(['a', target.addressPointer]);
  tags.push(['e', target.id]);
  tags.push(['p', target.pubkey, '', 'author']);
  if (context && context.trim()) tags.push(['context', context.trim().slice(0, 600)]);
  return tags;
}

/** A highlight must be `kind:9802` with non-empty `content` (the highlighted text). */
export function isValidHighlight(event: NostrEvent): boolean {
  return event.kind === HIGHLIGHT_KIND && event.content.trim().length > 0;
}
